import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Table } from "../../../../ui/components/Table";
import {
  FeatherAlertTriangle,
  FeatherCheckCircle,
  FeatherHelpCircle,
  FeatherInfo,
  FeatherSearch,
  FeatherX,
  FeatherXCircle,
} from "@subframe/core";
import {
  refreshGSTINVerification,
  verifyGSTIN,
  type GSTINVerificationResult,
} from "../../../../shared/data/api/gstinVerificationApi";
import { namesMatch } from "../../../../shared/utils/gstinUtils";
import type { BulkUploadKind } from "../hooks/useBulkUpload";

export interface GstinBulkVerifyRow {
  excel_row: number;
  payload: Record<string, unknown>;
}

/** Verification stamp written next to each row's payload in change_details.raw_json.rows. */
export interface GstinRowVerification {
  gstin: string;
  status: string;
  legal_name: string;
  trade_name: string;
  verified_at: string;
  name_match_confirmed: boolean;
}

export interface GstinBulkVerifyModalProps {
  open: boolean;
  kind: BulkUploadKind;
  rows: GstinBulkVerifyRow[];
  onCancel: () => void;
  onComplete: (stamped: Record<number, GstinRowVerification>) => void;
}

type RowVerifyStatus = "unverified" | "verifying" | "verified" | "failed";

interface RowVerifyState {
  status: RowVerifyStatus;
  result: GSTINVerificationResult | null;
  errorMessage: string;
  nameMatchConfirmed: boolean;
}

const IDLE_ROW_STATE: RowVerifyState = {
  status: "unverified",
  result: null,
  errorMessage: "",
  nameMatchConfirmed: false,
};

const TONE: Record<string, string> = {
  green: "border-success-200 bg-success-50 text-success-700",
  amber: "border-[#FDE68A] bg-[#FFFBEB] text-[#B45309]",
  red: "border-error-200 bg-error-50 text-error-700",
  gray: "border-neutral-200 bg-neutral-50 text-neutral-600",
};

function gstRecordLabel(result: GSTINVerificationResult | null): string {
  if (!result) return "";
  const legal = (result.legal_name || "").trim();
  const trade = (result.trade_name || "").trim();
  const showTrade = trade !== "" && trade.toLowerCase() !== legal.toLowerCase();
  return `GST record: ${legal}${showTrade ? ` (${trade})` : ""}`;
}

function StatusChip({
  tone,
  Icon,
  label,
}: {
  tone: keyof typeof TONE;
  Icon: React.ElementType;
  label: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-solid px-2.5 py-1 text-caption-bold font-caption-bold ${TONE[tone]}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

export const GstinBulkVerifyModal: React.FC<GstinBulkVerifyModalProps> = ({
  open,
  kind,
  rows,
  onCancel,
  onComplete,
}) => {
  const [rowStates, setRowStates] = useState<Record<number, RowVerifyState>>(
    {},
  );
  const [isVerifyingAll, setIsVerifyingAll] = useState(false);
  const runGenerationRef = useRef(0);

  const entityLabel = kind === "customers" ? "customer" : "vendor";

  const rowName = useCallback(
    (payload: Record<string, unknown>): string => {
      const value =
        kind === "customers" ? payload.customer_name : payload.vendor_legal_name;
      return typeof value === "string" ? value : "";
    },
    [kind],
  );

  const rowGstin = useCallback(
    (payload: Record<string, unknown>): string => {
      const value =
        kind === "customers" ? payload.gstin_vat_tax_id : payload.gstin;
      return typeof value === "string" ? value.trim() : "";
    },
    [kind],
  );

  const gstRows = useMemo(
    () => rows.filter((row) => row.payload?.gst_treatment === "business_gst"),
    [rows],
  );
  const nonGstCount = rows.length - gstRows.length;

  useEffect(() => {
    if (open) {
      runGenerationRef.current += 1;
      setRowStates({});
      setIsVerifyingAll(false);
    }
  }, [open, rows]);

  const stateFor = (excelRow: number): RowVerifyState =>
    rowStates[excelRow] ?? IDLE_ROW_STATE;

  const isRowActive = (state: RowVerifyState): boolean =>
    state.status === "verified" && state.result?.status === "Active";

  const isRowMismatch = (
    row: GstinBulkVerifyRow,
    state: RowVerifyState,
  ): boolean =>
    isRowActive(state) &&
    !namesMatch(
      rowName(row.payload),
      state.result?.legal_name || "",
      state.result?.trade_name,
    );

  const verifyRow = useCallback(
    async (excelRow: number, gstin: string, force = false) => {
      const generation = runGenerationRef.current;
      const applyState = (state: RowVerifyState) => {
        if (runGenerationRef.current !== generation) return;
        setRowStates((prev) => ({ ...prev, [excelRow]: state }));
      };
      applyState({ ...IDLE_ROW_STATE, status: "verifying" });
      try {
        const response = force
          ? await refreshGSTINVerification(gstin)
          : await verifyGSTIN(gstin);
        // Backend wraps: { success, data: { verification: {...}, cached }, message }
        const verification = response.data?.verification ?? response.data;
        if (response.success && verification) {
          applyState({
            ...IDLE_ROW_STATE,
            status: "verified",
            result: verification as GSTINVerificationResult,
          });
        } else {
          applyState({
            ...IDLE_ROW_STATE,
            status: "failed",
            errorMessage: response.message || "Verification failed",
          });
        }
      } catch (err: any) {
        const msg =
          err?.data?.message ||
          err?.message ||
          "An error occurred during verification";
        applyState({ ...IDLE_ROW_STATE, status: "failed", errorMessage: msg });
      }
    },
    [],
  );

  const handleVerifyAll = useCallback(async () => {
    if (isVerifyingAll) return;
    const pending = gstRows.filter((row) => {
      if (rowGstin(row.payload) === "") return false;
      const state = rowStates[row.excel_row];
      return !(
        state &&
        state.status === "verified" &&
        state.result?.status === "Active"
      );
    });
    if (pending.length === 0) return;
    const generation = runGenerationRef.current;
    setIsVerifyingAll(true);
    try {
      for (const row of pending) {
        if (runGenerationRef.current !== generation) break;
        await verifyRow(row.excel_row, rowGstin(row.payload));
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 150);
        });
      }
    } finally {
      setIsVerifyingAll(false);
    }
  }, [gstRows, isVerifyingAll, rowGstin, rowStates, verifyRow]);

  const confirmRowName = (excelRow: number) => {
    setRowStates((prev) => {
      const state = prev[excelRow];
      if (!state) return prev;
      return { ...prev, [excelRow]: { ...state, nameMatchConfirmed: true } };
    });
  };

  const verifiedActiveCount = gstRows.filter((row) =>
    isRowActive(stateFor(row.excel_row)),
  ).length;

  const blockedCount = gstRows.filter((row) => {
    const state = stateFor(row.excel_row);
    return (
      rowGstin(row.payload) === "" ||
      (state.status === "verified" && !isRowActive(state))
    );
  }).length;

  const canContinue = gstRows.every((row) => {
    if (rowGstin(row.payload) === "") return false;
    const state = stateFor(row.excel_row);
    if (!isRowActive(state)) return false;
    return !isRowMismatch(row, state) || state.nameMatchConfirmed;
  });

  const handleContinue = () => {
    const stamped: Record<number, GstinRowVerification> = {};
    for (const row of gstRows) {
      const state = stateFor(row.excel_row);
      if (state.status !== "verified" || !state.result) continue;
      stamped[row.excel_row] = {
        gstin: state.result.gstin || rowGstin(row.payload),
        status: state.result.status,
        legal_name: state.result.legal_name,
        trade_name: state.result.trade_name,
        verified_at: state.result.verified_at,
        name_match_confirmed: state.nameMatchConfirmed,
      };
    }
    onComplete(stamped);
  };

  return (
    <DialogLayout
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      className="flex max-h-[90vh] w-full min-w-[min(1040px,100vw-2rem)] max-w-[1040px] flex-col overflow-hidden"
    >
      <div className="flex max-h-[90vh] w-full flex-col overflow-hidden">
        <div className="flex w-full flex-none items-start justify-between gap-4 border-b border-solid border-neutral-border p-6">
          <div className="flex flex-col items-start gap-1 pr-8">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Verify GSTINs before review
            </span>
            <span className="text-body font-body text-subtext-color">
              Every Business GST {entityLabel} must have an active GSTIN before
              this batch can be sent for review.
            </span>
          </div>
          <IconButton size="small" icon={<FeatherX />} onClick={onCancel} />
        </div>

        <div className="flex min-h-0 w-full flex-1 flex-col gap-4 overflow-y-auto p-6">
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <Button
              icon={<FeatherSearch />}
              loading={isVerifyingAll}
              disabled={isVerifyingAll || gstRows.length === 0}
              onClick={() => void handleVerifyAll()}
            >
              Verify all ({gstRows.length})
            </Button>
            <span className="text-caption font-caption text-subtext-color">
              {verifiedActiveCount}/{gstRows.length} verified active
            </span>
          </div>

          <div className="w-full overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell className="w-16">Row</Table.HeaderCell>
                  <Table.HeaderCell className="min-w-[180px]">
                    Name
                  </Table.HeaderCell>
                  <Table.HeaderCell className="min-w-[150px]">
                    GSTIN
                  </Table.HeaderCell>
                  <Table.HeaderCell className="min-w-[300px]">
                    Status
                  </Table.HeaderCell>
                  <Table.HeaderCell className="min-w-[150px]">
                    Action
                  </Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {gstRows.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={5}>
                    <div className="flex w-full items-center justify-center py-8">
                      <span className="text-body font-body text-subtext-color">
                        No Business GST rows in this file.
                      </span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : (
                gstRows.map((row) => {
                  const state = stateFor(row.excel_row);
                  const name = rowName(row.payload);
                  const gstin = rowGstin(row.payload);
                  const missingGstin = gstin === "";
                  const mismatch = isRowMismatch(row, state);

                  let chip: React.ReactNode;
                  let detail: React.ReactNode = null;
                  let action: React.ReactNode = null;

                  if (missingGstin) {
                    chip = (
                      <StatusChip
                        tone="red"
                        Icon={FeatherXCircle}
                        label="GSTIN missing"
                      />
                    );
                    detail = (
                      <span className="text-caption font-caption text-error-700">
                        Fix this row in Excel and upload again.
                      </span>
                    );
                  } else if (state.status === "verifying") {
                    chip = (
                      <StatusChip
                        tone="gray"
                        Icon={FeatherSearch}
                        label="Verifying…"
                      />
                    );
                  } else if (state.status === "failed") {
                    chip = (
                      <StatusChip
                        tone="red"
                        Icon={FeatherXCircle}
                        label="Failed"
                      />
                    );
                    detail = (
                      <span className="text-caption font-caption text-error-700">
                        {state.errorMessage}
                      </span>
                    );
                    action = (
                      <Button
                        size="small"
                        variant="neutral-secondary"
                        disabled={isVerifyingAll}
                        onClick={() => void verifyRow(row.excel_row, gstin)}
                      >
                        Re-verify
                      </Button>
                    );
                  } else if (state.status === "verified") {
                    if (isRowActive(state)) {
                      if (mismatch && !state.nameMatchConfirmed) {
                        chip = (
                          <StatusChip
                            tone="amber"
                            Icon={FeatherAlertTriangle}
                            label="Name mismatch"
                          />
                        );
                        detail = (
                          <span className="whitespace-normal break-words text-caption font-caption text-subtext-color">
                            {gstRecordLabel(state.result)}
                          </span>
                        );
                        action = (
                          <Button
                            size="small"
                            variant="neutral-secondary"
                            disabled={isVerifyingAll}
                            onClick={() => confirmRowName(row.excel_row)}
                          >
                            Use anyway
                          </Button>
                        );
                      } else {
                        chip = (
                          <StatusChip
                            tone="green"
                            Icon={FeatherCheckCircle}
                            label={
                              mismatch
                                ? "Active · name confirmed"
                                : "Verified · Active"
                            }
                          />
                        );
                        if (mismatch) {
                          detail = (
                            <span className="whitespace-normal break-words text-caption font-caption text-subtext-color">
                              {gstRecordLabel(state.result)}
                            </span>
                          );
                        }
                      }
                    } else {
                      chip = (
                        <StatusChip
                          tone="red"
                          Icon={FeatherXCircle}
                          label={
                            state.result?.status?.trim()
                              ? state.result.status
                              : "Not active"
                          }
                        />
                      );
                      action = (
                        <Button
                          size="small"
                          variant="neutral-secondary"
                          disabled={isVerifyingAll}
                          onClick={() =>
                            void verifyRow(row.excel_row, gstin, true)
                          }
                        >
                          Re-verify
                        </Button>
                      );
                    }
                  } else {
                    chip = (
                      <StatusChip
                        tone="gray"
                        Icon={FeatherHelpCircle}
                        label="Not verified"
                      />
                    );
                    action = (
                      <Button
                        size="small"
                        disabled={isVerifyingAll}
                        onClick={() => void verifyRow(row.excel_row, gstin)}
                      >
                        Verify
                      </Button>
                    );
                  }

                  return (
                    <Table.Row key={row.excel_row}>
                      <Table.Cell className="w-16">
                        <span className="text-body font-body text-subtext-color">
                          {row.excel_row}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="min-w-[180px]">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {name || "—"}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="min-w-[150px]">
                        <span className="text-body font-body text-default-font">
                          {gstin || "—"}
                        </span>
                      </Table.Cell>
                      <Table.Cell className="min-w-[300px] h-auto min-h-[48px] py-2">
                        <div className="flex flex-col items-start gap-1">
                          {chip}
                          {detail}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="min-w-[150px]">
                        {action ?? (
                          <span className="text-caption font-caption text-subtext-color">
                            —
                          </span>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table>
          </div>

          {blockedCount > 0 ? (
            <div className="flex items-center gap-2 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
              <FeatherAlertTriangle className="h-4 w-4 flex-shrink-0 text-error-700" />
              <span className="text-caption font-caption text-error-700">
                {blockedCount} row{blockedCount !== 1 ? "s" : ""} cannot be sent
                for review because the GSTIN is not active on the GST portal.
                Remove or correct {blockedCount !== 1 ? "these rows" : "this row"}{" "}
                in your Excel file and upload it again — rows already verified
                will verify instantly on re-upload.
              </span>
            </div>
          ) : null}

          {nonGstCount > 0 ? (
            <div className="flex items-center gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-3">
              <FeatherInfo className="h-4 w-4 flex-shrink-0 text-subtext-color" />
              <span className="text-caption font-caption text-subtext-color">
                {nonGstCount} row{nonGstCount !== 1 ? "s" : ""} without GST skip
                verification and go for review as-is.
              </span>
            </div>
          ) : null}
        </div>

        <div className="flex w-full flex-none items-center justify-end gap-2 border-t border-solid border-neutral-border p-6">
          <Button variant="neutral-secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            disabled={!canContinue || isVerifyingAll}
            onClick={handleContinue}
          >
            Continue to review
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};
